import React from "react";
import { formatAddress, formatDecimal } from "../../../../hooks/Method";
import moment from "moment";

const PrintReturnReceipt = ({ data }) => {
  const org = data.org;
  return (
    <div>
      {/* Inline CSS */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .receipt-container {
              max-width: 850px;
              margin: 0 auto;
              padding: 40px;
              font-family: 'Roboto', 'Arial', sans-serif;
              background-color: #ffffff;
              border: none;
              box-shadow: none;
            }

            .receipt-header {
              text-align: center;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }

            .company-info {
              margin-bottom: 20px;
            }

            .company-info h2 {
              margin: 0;
              font-size: 26px;
              font-weight: 500;
              color: #111827;
              letter-spacing: 1.2px;
            }

            .company-info p {
              margin: 4px 0;
              font-size: 15px;
              color: #6b7280;
              font-weight: 400;
            }

            .receipt-title {
              font-size: 20px; 
              font-weight: 600;
              color: #111827;
              margin: 40px auto; /* Changed to auto for horizontal centering */
              border-bottom: 2px solid #ddd;
              width: fit-content;
              text-transform: uppercase;
              letter-spacing: 1.2px;
              text-align: center; /* Ensure text is centered */
            }

            .receipt-details {
              display: flex;
              justify-content: space-between;
              font-size: 15px;
              color: #4b5563;
              margin-top: 15px;
            }

            .receipt-details p {
              margin: 4px 0;
              font-weight: 400;
            }

            .receipt-body h3 {
              font-size: 18px;
              font-weight: 500;
              color: #111827;
              margin-bottom: 15px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }

            .receipt-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 30px;
            }

            .receipt-table th,
            .receipt-table td {
              border: 1px solid #e5e7eb;
              padding: 10px;
              text-align: left;
              font-size: 15px;
              color: #374151;
            }

            .receipt-table th {
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #111827;
            }

            .receipt-table td {
              font-weight: 400;
            }

            .receipt-totals {
              text-align: right;
              margin-top: 10px;
              font-size: 17px;
              font-weight: 600;
              color: #111827;
            }

            .receipt-totals p {
              margin: 6px 0;
            }

            .receipt-notes {
              margin-top: 30px;
              font-size: 15px;
              color: #6b7280;
              border-top: 1px solid #e5e7eb;
              padding-top: 15px;
              font-style: italic;
            }

            .receipt-notes p {
              margin: 4px 0;
            }

            .authorization-section {
              margin-top: 60px;
              padding-top: 10px;
              display: flex;
              justify-content: flex-end;
            }

            .signature-container {
              display: flex;
              flex-direction: column;
              align-items: flex-end;
              width: 250px;
            }

            .signature-line {
              border-bottom: 1px solid #6b7280;
              width: 200px;
              margin-bottom: 6px;
            }

            .signature-label {
              font-size: 14px;
              color: #111827;
              font-weight: 400;
              text-align: right;
            }

            .receipt-footer {
              text-align: center;
              margin-top: 20px;
              padding-top: 15px;
              border-top: 1px solid #e5e7eb;
              font-size: 14px;
              color: #6b7280;
              font-style: italic;
            }

            .receipt-footer p {
              margin: 4px 0;
            }

            .receipt-footer .tagline {
              font-weight: 500;
              color: #111827;
            }

            /* Media query for printing */
            @media print {
              .receipt-container {
                border: none;
                box-shadow: none;
                padding: 20px;
              }

              .receipt-footer {
                position: fixed;
                bottom: 0;
                width: 100%;
                text-align: center;
              }
            }
          `,
        }}
      />

      {/* Receipt Content */}
      <div className="receipt-container">
        {/* Receipt Header */}
        <div className="receipt-header">
          <div className="company-info">
            <h2>{org.orgName}</h2>
            <p>
              {formatAddress({
                address1: org.address1,
                address2: org.address2,
                city: org.city,
                state: org.state,
                zip: org.zip,
              })}
            </p>
            <p>
              Email: {org.email || "-"} | Phone: {org.phone || "-"}
            </p>
          </div>
          <h1 className="receipt-title">Supplier Return Receipt</h1>
          <div className="receipt-details">
            <div>
              <p>Receipt No: {data.returnNo}</p>
              <p>Order No: {data.orderNo}</p>
              <p>
                Return Date:{" "}
                {moment(data.returnDate).format("MMM DD, YYYY, h:mm A")}
              </p>
            </div>
            <div>
              <p>
                Supplier:{" "}
                {data.supplier &&
                  (data.supplier.company || data.supplier.splrName)}
              </p>
              <p>
                Prepared by:{" "}
                {data.user && data.user.firstName + " " + data.user.lastName}
              </p>
            </div>
          </div>
        </div>

        {/* Rest of the receipt content remains the same */}
        <div className="receipt-body">
          <h3>Returned Products</h3>
          <table className="receipt-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Return Qty</th>
                <th>Unit Price</th>
                <th>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.stockReturnItems.map((product, index) => (
                <tr key={index}>
                  <td>
                    <div style={{ display: "grid", gap: "1px" }}>
                      <span style={{ fontSize: "16px" }}>{product.label}</span>
                      <span style={{ fontSize: "14px" }}>
                        {product.subtitle}
                      </span>
                    </div>
                  </td>
                  <td>{product.qty}</td>
                  <td>$ {formatDecimal(product.unitPrice, true)}</td>
                  <td>
                    $ {formatDecimal(product.unitPrice * product.qty, true)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="receipt-totals">
            <p>Total Quantity: {formatDecimal(data.totalQty, true)}</p>
            <p>Total Amount: $ {formatDecimal(data.totalAmount, true)}</p>
          </div>

          <div className="receipt-notes">
            {data.note && <p>Note: {data.note}</p>}
            {data.keepRefundAsCredit && (
              <p>Keep the return amount as credit balance.</p>
            )}
          </div>
        </div>

        <div className="authorization-section">
          <div className="signature-container">
            <div className="signature-line"></div>
            <div className="signature-label">Authorized Signature</div>
          </div>
        </div>

        <div className="receipt-footer">
          <p className="tagline">{org.orgName} – Excellence in Service</p>
          <p>Thank you for your business!</p>
          <p>
            For inquiries, please contact us at {org.email} or {org.phone}.
          </p>
          <p>Generated on: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

export default PrintReturnReceipt;
